// Kakao OAuth Authentication Service

const KAKAO_AUTH_URL = 'https://kauth.kakao.com/oauth/authorize';
const KAKAO_TOKEN_URL = 'https://kauth.kakao.com/oauth/token';
const KAKAO_USER_INFO_URL = 'https://kapi.kakao.com/v2/user/me';

export interface KakaoAuthConfig {
  clientId: string;
  redirectUri: string;
}

export interface KakaoUser {
  id: number;
  kakao_account: {
    email?: string;
    profile?: {
      nickname?: string;
      profile_image_url?: string;
      thumbnail_image_url?: string;
    };
  };
}

export interface KakaoTokenResponse {
  access_token: string;
  token_type: string;
  refresh_token: string;
  expires_in: number;
  scope?: string;
  refresh_token_expires_in: number;
}

/**
 * Kakao 로그인 URL 생성
 */
export const getKakaoLoginUrl = (config: KakaoAuthConfig): string => {
  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: 'code',
  });

  return `${KAKAO_AUTH_URL}?${params.toString()}`;
};

/**
 * 인가 코드로 액세스 토큰 교환
 */
export const exchangeCodeForToken = async (
  code: string,
  config: KakaoAuthConfig
): Promise<KakaoTokenResponse> => {
  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    code,
  });

  // Client Secret이 설정되어 있으면 추가
  const clientSecret = process.env.REACT_APP_KAKAO_CLIENT_SECRET;
  if (clientSecret && clientSecret !== 'your_client_secret_here') {
    params.append('client_secret', clientSecret);
  }

  const response = await fetch(KAKAO_TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Failed to exchange code for token: ${error.error_description || error.error}`);
  }

  return response.json();
};

/**
 * 액세스 토큰으로 사용자 정보 조회
 */
export const getKakaoUserInfo = async (accessToken: string): Promise<KakaoUser> => {
  const response = await fetch(KAKAO_USER_INFO_URL, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Failed to get user info: ${error.msg || 'Unknown error'}`);
  }

  return response.json();
};

/**
 * Kakao 로그아웃
 */
export const kakaoLogout = async (accessToken: string): Promise<void> => {
  const response = await fetch('https://kapi.kakao.com/v1/user/logout', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    console.error('Failed to logout from Kakao');
  }
};

/**
 * 로컬 스토리지에 토큰 저장
 */
export const saveTokens = (tokens: KakaoTokenResponse): void => {
  localStorage.setItem('kakao_access_token', tokens.access_token);
  localStorage.setItem('kakao_refresh_token', tokens.refresh_token);
  localStorage.setItem('kakao_token_expires_at', String(Date.now() + tokens.expires_in * 1000));
};

/**
 * 로컬 스토리지에서 토큰 가져오기
 */
export const getStoredAccessToken = (): string | null => {
  const token = localStorage.getItem('kakao_access_token');
  const expiresAt = localStorage.getItem('kakao_token_expires_at');

  if (!token || !expiresAt) {
    return null;
  }

  // 토큰 만료 확인
  if (Date.now() >= parseInt(expiresAt)) {
    clearTokens();
    return null;
  }

  return token;
};

/**
 * 로컬 스토리지에서 토큰 삭제
 */
export const clearTokens = (): void => {
  localStorage.removeItem('kakao_access_token');
  localStorage.removeItem('kakao_refresh_token');
  localStorage.removeItem('kakao_token_expires_at');
};
